import { useCallback, useEffect, useState } from "react";
import { type Answers, type Scored, type Setup, emptyAnswers } from "../services/catalogApi";
import { type Details, type QueueEntry, emptyDetails } from "../components/catalog/assessment";

export const STORAGE_KEY = "contextral.catalog.v2";

export type Decision = "added" | "skipped";

/** Everything the catalog remembers between visits, in this browser only. */
export interface CatalogSession {
  answers: Answers;
  details: Details;
  /** Questions answered or skipped, so a returning visitor picks up where they left off. */
  done: string[];
  setup: Setup | null;
  queue: QueueEntry[];
  decided: Record<string, Decision>;
  /** Why a visitor said "Not for me", for the lead. */
  notes: Record<string, string>;
  viewed: string[];
  /** Services the catalog suggested for what the visitor said, and the ones they added to their plan. */
  offered: Scored[];
  services: string[];
  updatedAt: string;
}

export const emptySession = (): CatalogSession => ({
  answers: emptyAnswers(), details: emptyDetails(), done: [], setup: null, queue: [], decided: {}, notes: {},
  viewed: [], offered: [], services: [], updatedAt: new Date(0).toISOString(),
});

function isSession(value: unknown): value is CatalogSession {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return Array.isArray(v.done) && Array.isArray(v.queue) && Array.isArray(v.viewed) &&
    typeof v.answers === "object" && v.answers !== null && typeof v.decided === "object" && v.decided !== null;
}

export function loadSession(): CatalogSession {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySession();
    const parsed: unknown = JSON.parse(raw);
    if (!isSession(parsed)) return emptySession();
    const base = emptySession();
    return { ...base, ...parsed, answers: { ...base.answers, ...parsed.answers },
             details: { ...base.details, ...(parsed.details ?? {}) } };
  } catch {
    return emptySession(); // Private windows, blocked storage or corrupt data: start fresh.
  }
}

export function saveSession(session: CatalogSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage is a convenience; the page works without it.
  }
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function useCatalogSession() {
  const [session, setSession] = useState<CatalogSession>(emptySession);
  const [saved, setSaved] = useState<CatalogSession | null>(null);

  useEffect(() => {
    const stored = loadSession();
    if (stored.done.length) setSaved(stored);
  }, []);

  const update = useCallback((change: Partial<CatalogSession> | ((s: CatalogSession) => Partial<CatalogSession>)) => {
    setSession((current) => {
      const next = { ...current, ...(typeof change === "function" ? change(current) : change),
                     updatedAt: new Date().toISOString() };
      if (next.done.length || next.queue.length) saveSession(next);
      return next;
    });
  }, []);

  const restore = useCallback((from: CatalogSession) => {
    setSession(from);
    setSaved(null);
  }, []);

  const reset = useCallback(() => {
    clearSession();
    setSaved(null);
    setSession(emptySession());
  }, []);

  return { session, update, saved, restore, reset };
}
